import * as React from 'react'
import { Link } from 'react-router-dom'
import { pickPermissions, useQueryCompanyRoles } from '@/lib/api/company/roles'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { USERS_ROUTES } from '../../routes-typed'
import { groupLabel, has, isDriverRole, PERMISSION_GROUPS, permissionLabel, roleDescription, rolesKey } from './catalog'
import { ScopeBadge } from './components'

/** Plakietka roli na liście użytkowników; po najechaniu pokazuje, co rola daje. */
export const RoleBadge: React.FC<{ roleId: number; name: string; companyId: string; className: string }> = ({
  roleId,
  name,
  companyId,
  className,
}) => {
  const { t } = useLocalize()
  const { data } = useQueryCompanyRoles(companyId)
  const role = data?.roles.find((r) => r.id === roleId)
  const [anchor, setAnchor] = React.useState<DOMRect | null>(null)
  const hideTimer = React.useRef<number | undefined>(undefined)

  const show = (e: React.MouseEvent<HTMLElement>) => {
    window.clearTimeout(hideTimer.current)
    setAnchor(e.currentTarget.getBoundingClientRect())
  }
  const hide = () => {
    hideTimer.current = window.setTimeout(() => setAnchor(null), 150)
  }
  React.useEffect(() => () => window.clearTimeout(hideTimer.current), [])

  const set = role ? pickPermissions(role) : null
  const description = role ? roleDescription(t, role) : null

  return (
    <>
      <span className={className} onMouseEnter={show} onMouseLeave={hide} data-testid={`role-badge-${roleId}`}>
        {name}
      </span>
      {anchor && role && set && (
        <div
          role="tooltip"
          onMouseEnter={() => window.clearTimeout(hideTimer.current)}
          onMouseLeave={hide}
          onClick={(e) => e.stopPropagation()}
          className="tw-fixed tw-z-[9999] tw-w-[340px] tw-rounded-md tw-border tw-border-base-300 tw-bg-base-100 tw-p-3 tw-text-left tw-shadow-xl"
          style={{ top: anchor.bottom + 4, left: Math.min(anchor.left, window.innerWidth - 356) }}
        >
          <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2">
            <span className="tw-font-medium tw-text-content-base">{role.name}</span>
            <ScopeBadge role={role} />
          </div>
          {description && <p className="tw-mb-2 tw-text-xs tw-text-content-base tw-opacity-70">{description}</p>}
          {!isDriverRole(role) && (
            <ul className="tw-mb-2 tw-flex tw-flex-col tw-gap-1">
              {PERMISSION_GROUPS.map((group) => {
                const granted = group.flags.filter((f) => has(set, f))
                if (granted.length === 0) return null
                return (
                  <li key={group.key} className="tw-text-xs tw-text-content-base">
                    <span className="tw-opacity-60">{groupLabel(t, group.key)}: </span>
                    {granted.map((f) => permissionLabel(t, f)).join(', ')}
                  </li>
                )
              })}
              {PERMISSION_GROUPS.every((g) => g.flags.every((f) => !has(set, f))) && (
                <li className="tw-text-xs tw-text-content-base tw-opacity-60">{t(rolesKey('noPermissions'))}</li>
              )}
            </ul>
          )}
          <Link
            to={USERS_ROUTES.rolesList.buildPath({ companyId })}
            className="tw-text-xs tw-text-primary hover:tw-underline"
          >
            {t(rolesKey('tooltipMore'))}
          </Link>
        </div>
      )}
    </>
  )
}
